import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Row, Col, Card, Button, Input, Table, notification, Space, Radio,
  Typography, Spin, Modal, Tag, Select, Badge, Switch, Tooltip, Upload,
} from 'antd';
import {
  ArrowLeftOutlined, SendOutlined, ExclamationCircleOutlined,
  SearchOutlined, ReloadOutlined, FilterOutlined, TeamOutlined,
  CheckCircleOutlined, UploadOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import {
  readCampaign, sendTest, sendAll, sendSelected, filterCount, listMembers,
  listSourceMembers, sendExcel, listExcelLists, sendFromList, getListFilterCount,
} from './festivalCampaignService';
import './FestivalCampaign.css';

const { Title, Text } = Typography;
const { confirm } = Modal;
const { Option } = Select;

const SEND_MODE = { ALL: 'all', SELECTED: 'selected', TEST: 'test', EXCEL: 'excel' };

// Available recipient sources
const SOURCES = [
  { value: 'members',          label: 'Members (Main List)',              color: '#7a0000', desc: 'All registered members from sn_users' },
  { value: 'members_unpaid',   label: 'Members – Never Paid',             color: '#fa541c', desc: 'Active members who have never made any payment' },
  { value: 'members_irregular',label: 'Members – Not Paying Regularly',   color: '#fa8c16', desc: 'Active members whose last payment was more than 3 months ago' },
  { value: 'registration',     label: 'Registration Form List',           color: '#52c41a', desc: 'Contacts from the Registration Form' },
  { value: 'purnima',          label: 'Purnima Form List',                color: '#722ed1', desc: 'Contacts from the Purnima Form' },
  { value: 'vishesh_sahyogi',  label: 'Vishesh Sahyogi List',             color: '#eb2f96', desc: 'Vishesh Sahyogi members' },
  { value: 'visitor',          label: 'Visitor Hub List',                 color: '#13c2c2', desc: 'Visitors registered in VisitorHub' },
  { value: 'exam_result',      label: 'Exam Results List',                color: '#faad14', desc: 'Students from Competition Exam Results' },
];

const MEMBER_SOURCES = ['members', 'members_unpaid', 'members_irregular'];

const DEFAULT_FILTERS = {
  status:       'active',
  address_type: 'permanent',
  village:      '',
  tehsil:       '',
  district:     '',
  occupation:   '',
  occ_district: '',
  phone:        '',
};

export default function SendCampaign() {
  const { id } = useParams();
  const history = useHistory();

  const [campaign, setCampaign]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [sendMode, setSendMode]   = useState(SEND_MODE.TEST);
  const [sending, setSending]     = useState(false);

  // Test mode
  const [testPhone, setTestPhone] = useState('');

  // Source selection
  const [source, setSource]               = useState('members');
  const [sourceSearch, setSourceSearch]   = useState('');

  // Filters (shared between Send All + Send Selected table)
  const [filters, setFilters]             = useState(DEFAULT_FILTERS);
  const [memberCount, setMemberCount]     = useState(null);
  const [countLoading, setCountLoading]   = useState(false);
  // Skip members who already received this campaign successfully (default ON)
  const [skipSent, setSkipSent]           = useState(true);

  // Selected mode
  const [users, setUsers]                   = useState([]);
  const [usersLoading, setUsersLoading]     = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [userPagination, setUserPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  // Excel mode
  const [excelFile, setExcelFile]           = useState(null);
  const [excelSubMode, setExcelSubMode]     = useState('upload'); // 'upload' | 'saved'
  const [savedLists, setSavedLists]         = useState([]);
  const [savedListsLoading, setSavedListsLoading] = useState(false);
  const [selectedListId, setSelectedListId]       = useState(null);
  const [listPaymentFilter, setListPaymentFilter] = useState('all');
  const [listSkipSent, setListSkipSent]           = useState(false);
  const [listFilteredCount, setListFilteredCount] = useState(null); // { count, total, excluded }
  const [listCountLoading, setListCountLoading]   = useState(false);

  useEffect(() => { loadCampaign(); }, [id]);

  useEffect(() => {
    if (sendMode === SEND_MODE.SELECTED) {
      loadUsers(1);
    }
    if (sendMode === SEND_MODE.EXCEL) {
      loadSavedLists();
    }
  }, [sendMode]); // eslint-disable-line

  const loadSavedLists = async () => {
    setSavedListsLoading(true);
    try {
      const res = await listExcelLists();
      if (res.status) setSavedLists(res.result);
    } catch (err) {
      console.error('Could not load saved Excel lists', err.message);
    } finally {
      setSavedListsLoading(false);
    }
  };

  const handleListCount = async () => {
    if (!selectedListId) return;
    setListCountLoading(true);
    try {
      const res = await getListFilterCount(id, selectedListId, listPaymentFilter, listSkipSent);
      if (res.status) setListFilteredCount(res);
      else notification.error({ message: res.message || 'Count failed' });
    } catch (err) {
      notification.error({ message: 'Could not fetch count', description: err.message });
    } finally {
      setListCountLoading(false);
    }
  };

  const loadCampaign = async () => {
    try {
      const res = await readCampaign(id);
      if (res.status) setCampaign(res.result);
      else notification.error({ message: 'Campaign not found' });
    } catch (err) {
      notification.error({ message: 'Failed to load campaign', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const isMemberSource = MEMBER_SOURCES.includes(source);

  const loadUsers = useCallback(async (page = 1) => {
    setUsersLoading(true);
    try {
      const params = isMemberSource
        ? { source, ...filters, page, limit: 15 }
        : { source, search: sourceSearch, page, limit: 15 };
      const res = await listSourceMembers(params);
      if (res.status) {
        setUsers(res.result.map((u) => ({ ...u, key: u.id || u.phone_no || u.phone })));
        setUserPagination((prev) => ({ ...prev, current: page, total: res.pagination?.total || 0 }));
        setMemberCount(res.pagination?.total || 0);
      }
    } catch (err) {
      notification.error({ message: 'Failed to load contacts' });
    } finally {
      setUsersLoading(false);
    }
  }, [filters, source, sourceSearch, isMemberSource]); // eslint-disable-line

  const handleFetchCount = async () => {
    setCountLoading(true);
    try {
      const payload = isMemberSource
        ? { source, ...filters, campaign_id: id, skip_sent: skipSent }
        : { source, search: sourceSearch, campaign_id: id, skip_sent: skipSent };
      const res = await filterCount(payload);
      if (res.status) setMemberCount(res.count);
    } catch (err) {
      notification.error({ message: 'Could not fetch count' });
    } finally {
      setCountLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSourceSearch('');
    setMemberCount(null);
    setSelectedRowKeys([]);
  };

  const handleSourceChange = (val) => {
    setSource(val);
    setMemberCount(null);
    setSelectedRowKeys([]);
    setSourceSearch('');
  };

  const downloadSampleExcel = () => {
    const rows = [
      ['phone', 'name', 'village', 'district', 'tehsil', 'address', 'house_no', 'occupation', 'father_name'],
      ['9876543210', 'राम लाल', 'बड़ागाँव', 'जोधपुर', 'बिलाड़ा', 'बड़ागाँव, बिलाड़ा, जोधपुर', 'मकान नं. 12', 'किसान', 'श्याम लाल'],
      ['9123456789', 'Shyam Kumar', 'Jaipur', 'Jaipur', 'Jaipur', 'Jaipur', 'House 5', 'Businessman', 'Ram Kumar'],
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'festival_campaign_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateFilter = (key, value) =>
    setFilters(prev => ({ ...prev, [key]: value }));

  const handleSend = () => {
    if (sendMode === SEND_MODE.TEST) {
      if (!testPhone || testPhone.length < 10) {
        notification.warning({ message: 'Enter a valid 10-digit phone number' });
        return;
      }
      confirm({
        title: `Send test poster to ${testPhone}?`,
        icon: <ExclamationCircleOutlined />,
        onOk: async () => {
          setSending(true);
          try {
            const res = await sendTest(id, testPhone);
            if (res.status) {
              notification.success({ message: 'Test poster sent!', description: `Sent to ${testPhone}` });
            } else {
              notification.error({ message: res.message || 'Send failed' });
            }
          } catch (err) {
            notification.error({ message: 'Send error', description: err.message });
          } finally {
            setSending(false);
          }
        },
      });

    } else if (sendMode === SEND_MODE.ALL) {
      const srcInfo = SOURCES.find(s => s.value === source);
      const countLabel = memberCount != null ? `${memberCount} contacts` : 'filtered contacts';
      confirm({
        title: `Send poster to ${countLabel}?`,
        content: (
          <div>
            <p><b>Source:</b> <Tag color={srcInfo?.color}>{srcInfo?.label}</Tag></p>
            {isMemberSource && (
              <ul style={{ paddingLeft: 16 }}>
                <li>Status: <b>{filters.status}</b></li>
                {filters.village      && <li>Village: <b>{filters.village}</b></li>}
                {filters.district     && <li>District: <b>{filters.district}</b></li>}
                {filters.occupation   && <li>Occupation: <b>{filters.occupation}</b></li>}
                {filters.phone        && <li>Mobile: <b>{filters.phone}</b></li>}
              </ul>
            )}
            {!isMemberSource && sourceSearch && <p>Search: <b>{sourceSearch}</b></p>}
            <p style={{ color: skipSent ? '#52c41a' : '#fa8c16', marginBottom: 4, fontWeight: 500 }}>
              {skipSent
                ? '✓ Already-sent contacts will be skipped'
                : '⚠ All matching contacts will be sent — including those already sent'}
            </p>
            <p style={{ color: '#ff4d4f', marginBottom: 0 }}>Runs in the background and cannot be undone.</p>
          </div>
        ),
        icon: <ExclamationCircleOutlined />,
        okType: 'danger',
        okText: 'Yes, Send Now',
        width: 480,
        onOk: async () => {
          setSending(true);
          try {
            const payload = isMemberSource
              ? { source, ...filters, skip_sent: skipSent }
              : { source, search: sourceSearch, skip_sent: skipSent };
            const res = await sendAll(id, payload);
            if (res.status) {
              notification.success({
                message: `Sending started! (${res.total} members)`,
                description: res.message,
                duration: 10,
              });
            } else {
              notification.error({ message: res.message || 'Send failed' });
            }
          } catch (err) {
            notification.error({ message: 'Send error', description: err.message });
          } finally {
            setSending(false);
          }
        },
      });

    } else if (sendMode === SEND_MODE.SELECTED) {
      if (!selectedRowKeys.length) {
        notification.warning({ message: 'Select at least one member' });
        return;
      }
      confirm({
        title: `Send poster to ${selectedRowKeys.length} selected members?`,
        icon: <ExclamationCircleOutlined />,
        onOk: async () => {
          setSending(true);
          try {
            const res = await sendSelected(id, selectedRowKeys);
            if (res.status) {
              notification.success({ message: 'Sending started!', description: res.message, duration: 6 });
              setSelectedRowKeys([]);
            } else {
              notification.error({ message: res.message || 'Send failed' });
            }
          } catch (err) {
            notification.error({ message: 'Send error', description: err.message });
          } finally {
            setSending(false);
          }
        },
      });

    } else if (sendMode === SEND_MODE.EXCEL) {
      if (excelSubMode === 'upload') {
        if (!excelFile) {
          notification.warning({ message: 'Please select an Excel file first' });
          return;
        }
        confirm({
          title: `Send poster to contacts from "${excelFile.name}"?`,
          content: (
            <div>
              <p>The backend will read all rows from the Excel file and send the campaign poster to each valid phone number.</p>
              <p style={{ color: '#fa8c16', marginBottom: 0 }}>Runs in the background and cannot be undone.</p>
            </div>
          ),
          icon: <ExclamationCircleOutlined />,
          okType: 'danger',
          okText: 'Yes, Send Now',
          onOk: async () => {
            setSending(true);
            try {
              const res = await sendExcel(id, excelFile);
              if (res.status) {
                notification.success({
                  message: `Sending started! (${res.total} contacts)`,
                  description: res.message,
                  duration: 10,
                });
                setExcelFile(null);
              } else {
                notification.error({ message: res.message || 'Send failed' });
              }
            } catch (err) {
              notification.error({ message: 'Send error', description: err.message });
            } finally {
              setSending(false);
            }
          },
        });
      } else {
        // saved list mode
        if (!selectedListId) {
          notification.warning({ message: 'Please select a saved list' });
          return;
        }
        const list = savedLists.find(l => l.id === selectedListId);
        const filterLabel =
          listPaymentFilter === 'unpaid_only' ? 'Unpaid Only'
          : listPaymentFilter === 'paid_only' ? 'Paid Only'
          : 'All Contacts';
        const countLine = listFilteredCount
          ? `${listFilteredCount.count.toLocaleString('en-IN')} contacts will be sent (${listFilteredCount.excluded} excluded by filter).`
          : `Up to ${Number(list?.valid_contacts || 0).toLocaleString('en-IN')} contacts (run Count for exact number).`;

        confirm({
          title: `Send poster using saved list "${list?.name}"?`,
          content: (
            <div>
              <p><b>Filter:</b> <Tag color="blue">{filterLabel}</Tag></p>
              <p><b>Skip already sent:</b> {listSkipSent ? <Tag color="green">ON</Tag> : <Tag>OFF</Tag>}</p>
              <p>{countLine}</p>
              <p style={{ color: '#fa8c16', marginBottom: 0 }}>Runs in the background and cannot be undone.</p>
            </div>
          ),
          icon: <ExclamationCircleOutlined />,
          okType: 'danger',
          okText: 'Yes, Send Now',
          width: 440,
          onOk: async () => {
            setSending(true);
            try {
              const res = await sendFromList(id, selectedListId, listPaymentFilter, listSkipSent);
              if (res.status) {
                notification.success({
                  message: `Sending started! (${res.total} contacts)`,
                  description: `Filter: ${res.filter}. ${res.excluded} excluded. ${res.message}`,
                  duration: 10,
                });
              } else {
                notification.error({ message: res.message || 'Send failed' });
              }
            } catch (err) {
              notification.error({ message: 'Send error', description: err.message });
            } finally {
              setSending(false);
            }
          },
        });
      }
    }
  };

  // ── Source selector card ──────────────────────────────────────────────────
  const SourceSelector = () => {
    const srcInfo = SOURCES.find(s => s.value === source);
    return (
      <Card
        size="small"
        style={{ marginBottom: 16, background: '#f0f5ff', borderColor: '#adc6ff' }}
        title={<Space><TeamOutlined style={{ color: '#7a0000' }} /><Text strong>Recipient Source</Text></Space>}
      >
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} md={10}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Select list to send to</Text>
            <Select
              value={source}
              onChange={handleSourceChange}
              style={{ width: '100%' }}
              size="small"
            >
              {SOURCES.map(s => (
                <Option key={s.value} value={s.value}>
                  <Space>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                    {s.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Col>
          {srcInfo && (
            <Col xs={24} md={14}>
              <Tag color={srcInfo.color} style={{ marginTop: 20, fontSize: 12 }}>{srcInfo.desc}</Tag>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  // ── Filter panel (shared between ALL and SELECTED modes) ─────────────────
  const FilterPanel = () => (
    <Card
      size="small"
      style={{ marginBottom: 20, background: '#fafafa', borderColor: '#d9d9d9' }}
      title={
        <Space>
          <FilterOutlined style={{ color: '#7a0000' }} />
          <Text strong>Recipient Filters</Text>
          {memberCount != null && (
            <Badge count={memberCount} style={{ backgroundColor: '#52c41a' }} overflowCount={99999} />
          )}
        </Space>
      }
      extra={
        <Space>
          <Button size="small" onClick={resetFilters}>Reset</Button>
          <Button size="small" onClick={handleFetchCount} loading={countLoading} icon={<TeamOutlined />}>
            Count
          </Button>
          {sendMode === SEND_MODE.SELECTED && (
            <Button size="small" type="primary" onClick={() => loadUsers(1)} loading={usersLoading}>
              Apply & Load
            </Button>
          )}
        </Space>
      }
    >
      {/* Non-member sources: simple search only */}
      {!isMemberSource && (
        <Row gutter={[12, 8]}>
          <Col xs={24} md={10}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Search (name / phone / village)</Text>
            <Input
              size="small"
              placeholder="Leave empty to include all"
              value={sourceSearch}
              onChange={(e) => setSourceSearch(e.target.value)}
              allowClear
            />
          </Col>
        </Row>
      )}

      {/* Member sources: full filter set */}
      {isMemberSource && (
        <Row gutter={[12, 8]}>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Member Status</Text>
            <Select value={filters.status} onChange={(v) => updateFilter('status', v)} style={{ width: '100%' }} size="small">
              <Option value="active">Active Only</Option>
              <Option value="inactive">Inactive Only</Option>
              <Option value="both">Active + Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Address Type</Text>
            <Select value={filters.address_type} onChange={(v) => updateFilter('address_type', v)} style={{ width: '100%' }} size="small">
              <Option value="permanent">Permanent</Option>
              <Option value="temporary">Temporary</Option>
              <Option value="both">Both</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Village / City</Text>
            <Input size="small" placeholder="बड़ागाँव or Badaganv" value={filters.village} onChange={(e) => updateFilter('village', e.target.value)} allowClear />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Tehsil</Text>
            <Input size="small" placeholder="बिलाड़ा" value={filters.tehsil} onChange={(e) => updateFilter('tehsil', e.target.value)} allowClear />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>District</Text>
            <Input size="small" placeholder="जोधपुर" value={filters.district} onChange={(e) => updateFilter('district', e.target.value)} allowClear />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Occupation</Text>
            <Input size="small" placeholder="किसान or Doctor" value={filters.occupation} onChange={(e) => updateFilter('occupation', e.target.value)} allowClear />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Occ. District</Text>
            <Input size="small" placeholder="जोधपुर" value={filters.occ_district} onChange={(e) => updateFilter('occ_district', e.target.value)} allowClear />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Mobile</Text>
            <Input size="small" placeholder="98765 or full number" value={filters.phone} onChange={(e) => updateFilter('phone', e.target.value)} allowClear maxLength={15} />
          </Col>
        </Row>
      )}

      {/* Skip already sent toggle (Send All mode only) */}
      {sendMode === SEND_MODE.ALL && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Switch size="small" checked={skipSent} onChange={setSkipSent} checkedChildren={<CheckCircleOutlined />} />
          <Text style={{ fontSize: 13 }}>
            <b>Skip already sent</b>
            {skipSent
              ? <span style={{ color: '#52c41a', marginLeft: 6 }}>ON — already-sent contacts are excluded</span>
              : <span style={{ color: '#fa8c16', marginLeft: 6 }}>OFF — all matching contacts will receive it again</span>}
          </Text>
          <Tooltip title="Uses the message log to detect who already received this campaign successfully. Keep ON to safely resume a partial send without duplicates.">
            <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>ⓘ</Text>
          </Tooltip>
        </div>
      )}

      {memberCount != null && (
        <div style={{ marginTop: 10 }}>
          <Tag color="green" icon={<TeamOutlined />} style={{ fontSize: 13 }}>
            {memberCount} {skipSent && sendMode === SEND_MODE.ALL ? 'remaining (unsent)' : 'contacts match'}
          </Tag>
        </div>
      )}
    </Card>
  );

  // ── Member table columns ──────────────────────────────────────────────────
  const userColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v) => v || '-' },
    { title: 'Phone', dataIndex: 'phone_no', key: 'phone_no', width: 120 },
    { title: 'Village (Perm)', dataIndex: 'per_village', key: 'per_village', render: (v) => v || '-' },
    { title: 'Tehsil', dataIndex: 'per_tehsil', key: 'per_tehsil', width: 100, render: (v) => v || '-' },
    { title: 'District', dataIndex: 'per_district', key: 'per_district', width: 100, render: (v) => v || '-' },
    {
      title: 'Temp City', dataIndex: 'tem_village', key: 'tem_village', width: 100,
      render: (v) => v ? <Tag color="purple">{v}</Tag> : '-'
    },
    {
      title: 'Status', dataIndex: 'member_status', key: 'member_status', width: 80,
      render: (v) => <Tag color={v === 'Active' ? 'green' : 'red'}>{v}</Tag>
    },
    {
      title: 'Occupation', dataIndex: 'occupation', key: 'occupation', width: 110,
      render: (v) => v || '-'
    },
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  return (
    <div className="layout-content">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/festival-campaign')}>Back</Button>
                <Title level={4} style={{ margin: 0 }}>
                  Send: <Text type="secondary">{campaign?.name}</Text>
                </Title>
              </Space>
            }
          >
            {/* Campaign summary */}
            <Row gutter={16} style={{ marginBottom: 20 }}>
              <Col xs={12} md={4}>
                <Text type="secondary">Festival: </Text><Tag>{campaign?.festival_type}</Tag>
              </Col>
              <Col xs={12} md={4}>
                <Text type="secondary">Media: </Text>
                <Tag color={campaign?.media_type === 'image' ? 'geekblue' : campaign?.media_type === 'video' ? 'purple' : 'orange'}>
                  {campaign?.media_type || 'image'}
                </Tag>
              </Col>
              <Col xs={12} md={4}>
                <Text type="secondary">Template: </Text><Text strong>{campaign?.template_name}</Text>
              </Col>
              <Col xs={12} md={4}>
                <Text type="secondary">Status: </Text>
                <Tag color={campaign?.status === 'sent' ? 'green' : 'blue'}>{campaign?.status}</Tag>
              </Col>
              {campaign?.poster_url && campaign?.media_type !== 'document' && (
                <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                  {campaign.media_type === 'video' ? (
                    <video src={campaign.poster_url} style={{ height: 80, borderRadius: 4, border: '1px solid #d9d9d9' }} />
                  ) : (
                    <img src={campaign.poster_url} alt="poster" style={{ height: 80, borderRadius: 4, objectFit: 'cover', border: '1px solid #d9d9d9' }} />
                  )}
                </Col>
              )}
            </Row>

            {/* Send mode selector */}
            <div style={{ marginBottom: 20 }}>
              <Radio.Group
                value={sendMode}
                onChange={(e) => setSendMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="large"
              >
                <Radio.Button value={SEND_MODE.TEST}>Send Test</Radio.Button>
                <Radio.Button value={SEND_MODE.ALL}>Send to All (Filtered)</Radio.Button>
                <Radio.Button value={SEND_MODE.SELECTED}>Send to Selected</Radio.Button>
                <Radio.Button value={SEND_MODE.EXCEL}>
                  <FileExcelOutlined /> Upload Excel
                </Radio.Button>
              </Radio.Group>
            </div>

            {/* ── TEST MODE ── */}
            {sendMode === SEND_MODE.TEST && (
              <Card size="small" title="Test Send" style={{ maxWidth: 500 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Send a sample poster to a specific phone number (must be a registered member).
                </Text>
                <Input
                  prefix="+91"
                  placeholder="Enter 10-digit phone number"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  maxLength={10}
                  style={{ marginBottom: 16, width: 300 }}
                />
                <br />
                <Button icon={<SendOutlined />} loading={sending} onClick={handleSend} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>
                  Send Test Poster
                </Button>
              </Card>
            )}

            {/* ── SEND ALL MODE ── */}
            {sendMode === SEND_MODE.ALL && (
              <div>
                <SourceSelector />
                <FilterPanel />
                <Card size="small" style={{ maxWidth: 540 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Select a source and filters above. Click "Count" to preview recipients.
                    Sends run in the background with a 500ms delay per contact.
                  </Text>
                  <Button
                    type="primary"
                    danger
                    icon={<SendOutlined />}
                    loading={sending}
                    onClick={handleSend}
                    size="large"
                    disabled={memberCount === 0}
                  >
                    Send to {memberCount != null ? `${memberCount} Contacts` : 'Filtered Contacts'}
                  </Button>
                </Card>
              </div>
            )}

            {/* ── EXCEL MODE ── */}
            {sendMode === SEND_MODE.EXCEL && (
              <Card size="small" title={<Space><FileExcelOutlined style={{ color: '#52c41a' }} /><span>Send via Excel</span></Space>} style={{ maxWidth: 580 }}>
                {/* Sub-mode toggle */}
                <div style={{ marginBottom: 16 }}>
                  <Radio.Group
                    value={excelSubMode}
                    onChange={(e) => { setExcelSubMode(e.target.value); setExcelFile(null); setSelectedListId(null); }}
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="upload"><UploadOutlined /> Upload New File</Radio.Button>
                    <Radio.Button value="saved"><FileExcelOutlined /> Use Saved List</Radio.Button>
                  </Radio.Group>
                </div>

                {/* ─ Upload New File ─ */}
                {excelSubMode === 'upload' && (
                  <>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                      Upload an Excel file (.xlsx / .xls) with external contacts. These do <b>not</b> need to be registered members.
                    </Text>

                    {/* Format guide */}
                    <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <Text strong style={{ fontSize: 12 }}>Required column:</Text>
                          <Tag color="red" style={{ marginLeft: 6 }}>phone</Tag>
                          <br />
                          <Text strong style={{ fontSize: 12 }}>Optional columns:</Text>
                          {['name', 'village', 'district', 'tehsil', 'address', 'house_no', 'occupation', 'father_name'].map(c => (
                            <Tag key={c} color="blue" style={{ marginLeft: 4, marginTop: 4 }}>{c}</Tag>
                          ))}
                          <br />
                          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                            Column names are case-insensitive. Phone numbers must be 10 digits.
                          </Text>
                        </div>
                        <Button
                          size="small"
                          icon={<FileExcelOutlined style={{ color: '#52c41a' }} />}
                          onClick={downloadSampleExcel}
                          style={{ marginLeft: 12, whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          Download Sample
                        </Button>
                      </div>
                    </div>

                    <Upload
                      accept=".xlsx,.xls"
                      beforeUpload={(file) => { setExcelFile(file); return false; }}
                      onRemove={() => setExcelFile(null)}
                      fileList={excelFile ? [{ uid: '1', name: excelFile.name, status: 'done' }] : []}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Select Excel File</Button>
                    </Upload>

                    {excelFile && (
                      <Tag color="green" style={{ marginTop: 10, fontSize: 13 }}>
                        <FileExcelOutlined /> {excelFile.name}
                      </Tag>
                    )}

                    <div style={{ marginTop: 16 }}>
                      <Button
                        type="primary"
                        danger
                        icon={<SendOutlined />}
                        loading={sending}
                        onClick={handleSend}
                        disabled={!excelFile}
                        size="large"
                      >
                        Send to Excel Contacts
                      </Button>
                    </div>
                  </>
                )}

                {/* ─ Use Saved List ─ */}
                {excelSubMode === 'saved' && (
                  <>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                      Pick a previously uploaded Excel list. Use filters to control who receives this campaign.
                    </Text>

                    {/* List selector */}
                    <Select
                      placeholder="Select a saved list..."
                      loading={savedListsLoading}
                      style={{ width: '100%', marginBottom: 12 }}
                      value={selectedListId}
                      onChange={(v) => { setSelectedListId(v); setListFilteredCount(null); }}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                      }
                      options={savedLists.map(l => ({
                        value: l.id,
                        label: `${l.name} (${Number(l.valid_contacts).toLocaleString('en-IN')} contacts)`,
                      }))}
                      notFoundContent={savedListsLoading ? <Spin size="small" /> : 'No saved lists found'}
                    />

                    {/* List summary */}
                    {selectedListId && (() => {
                      const list = savedLists.find(l => l.id === selectedListId);
                      if (!list) return null;
                      let cols = list.detected_columns;
                      if (typeof cols === 'string') { try { cols = JSON.parse(cols); } catch { cols = []; } }
                      return (
                        <div style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, padding: '8px 12px', marginBottom: 14 }}>
                          <Space wrap>
                            <Text style={{ fontSize: 12 }}><b>Rows:</b> {Number(list.total_rows).toLocaleString('en-IN')}</Text>
                            <Text style={{ fontSize: 12 }}><b>Valid phones:</b> <Tag color="green">{Number(list.valid_contacts).toLocaleString('en-IN')}</Tag></Text>
                            {Array.isArray(cols) && cols.length > 0 && cols.slice(0, 5).map(c => <Tag key={c} color="blue" style={{ marginRight: 2 }}>{c}</Tag>)}
                          </Space>
                        </div>
                      );
                    })()}

                    {/* ── Payment Filter ──────────────────────────────────── */}
                    <div style={{ marginBottom: 14 }}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
                        <b>Payment Status Filter</b> — cross-reference with payment records
                      </Text>
                      <Radio.Group
                        value={listPaymentFilter}
                        onChange={e => { setListPaymentFilter(e.target.value); setListFilteredCount(null); }}
                        optionType="button"
                        buttonStyle="outlined"
                        size="small"
                      >
                        <Radio.Button value="all">
                          <TeamOutlined /> All Contacts
                        </Radio.Button>
                        <Radio.Button value="unpaid_only">
                          Unpaid Only
                        </Radio.Button>
                        <Radio.Button value="paid_only">
                          Paid Only
                        </Radio.Button>
                      </Radio.Group>
                      <div style={{ marginTop: 4, fontSize: 11, color: '#8c8c8c' }}>
                        {listPaymentFilter === 'all'         && 'Send to every valid phone in the list, regardless of payment history.'}
                        {listPaymentFilter === 'unpaid_only' && 'Only send to phones NOT found in payment records — ideal for overdue reminders.'}
                        {listPaymentFilter === 'paid_only'   && 'Only send to phones that have at least one payment record.'}
                      </div>
                    </div>

                    {/* ── Skip Already Sent ───────────────────────────────── */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                      <Switch
                        size="small"
                        checked={listSkipSent}
                        onChange={v => { setListSkipSent(v); setListFilteredCount(null); }}
                        checkedChildren={<CheckCircleOutlined />}
                      />
                      <Text style={{ fontSize: 13 }}>
                        <b>Skip already sent</b>
                        {listSkipSent
                          ? <span style={{ color: '#52c41a', marginLeft: 6 }}>ON — contacts who already received this campaign will be skipped</span>
                          : <span style={{ color: '#fa8c16', marginLeft: 6 }}>OFF — all filtered contacts will be sent regardless of history</span>}
                      </Text>
                    </div>

                    {/* ── Count preview ────────────────────────────────────── */}
                    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        icon={<TeamOutlined />}
                        loading={listCountLoading}
                        disabled={!selectedListId}
                        onClick={handleListCount}
                      >
                        Count Recipients
                      </Button>
                      {listFilteredCount && (
                        <Space>
                          <Tag color="green" style={{ fontSize: 13 }}>
                            {listFilteredCount.count.toLocaleString('en-IN')} will be sent
                          </Tag>
                          {listFilteredCount.excluded > 0 && (
                            <Tag color="orange">
                              {listFilteredCount.excluded.toLocaleString('en-IN')} excluded
                            </Tag>
                          )}
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            of {listFilteredCount.total.toLocaleString('en-IN')} total
                          </Text>
                        </Space>
                      )}
                    </div>

                    <Button
                      type="primary"
                      danger
                      icon={<SendOutlined />}
                      loading={sending}
                      onClick={handleSend}
                      disabled={!selectedListId}
                      size="large"
                    >
                      Send to{listFilteredCount ? ` ${listFilteredCount.count.toLocaleString('en-IN')}` : ''} Contacts
                    </Button>
                  </>
                )}
              </Card>
            )}

            {/* ── SELECTED MODE ── */}
            {sendMode === SEND_MODE.SELECTED && (
              <div>
                <SourceSelector />
                <FilterPanel />

                <Space style={{ marginBottom: 12 }}>
                  <Input.Search
                    placeholder="Search by name / village..."
                    allowClear
                    onSearch={(v) => { updateFilter('village', v); loadUsers(1); }}
                    style={{ width: 260 }}
                    prefix={<SearchOutlined />}
                  />
                  <Button icon={<ReloadOutlined />} onClick={() => loadUsers(1)}>Refresh</Button>
                  {selectedRowKeys.length > 0 && (
                    <Tag color="blue" style={{ fontSize: 13 }}>{selectedRowKeys.length} selected</Tag>
                  )}
                </Space>

                <Table
                  rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                    preserveSelectedRowKeys: true,
                  }}
                  columns={userColumns}
                  dataSource={users}
                  loading={usersLoading}
                  rowKey="id"
                  size="small"
                  scroll={{ x: 800 }}
                  pagination={{
                    current: userPagination.current,
                    pageSize: userPagination.pageSize,
                    total: userPagination.total,
                    showTotal: (t) => `${t} members`,
                    onChange: (page) => loadUsers(page),
                  }}
                />

                <div style={{ marginTop: 16 }}>
                  <Button
                    icon={<SendOutlined />}
                    loading={sending}
                    onClick={handleSend}
                    disabled={!selectedRowKeys.length}
                    size="large"
                    style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
                  >
                    Send to {selectedRowKeys.length || 0} Selected Members
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
